import { forwardRef, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useCountdown from '@/features/laneChooser/hooks/useCountdown';
import { useLaneChooserTheme } from '@/features/laneChooser/theme';

import Tooltip, { TooltipColors, TooltipSizes } from '@/shared/components/tooltip/Tooltip';
import LaneChooserStage from '@/features/laneChooser/components/LaneChooserStage';
import Progress from '@/features/laneChooser/components/progress/Progress';

import type { CSSProperties } from 'react';
import type {
	LaneChooserAnswerResolution,
	LaneChooserQuestionPayload,
	LaneChooserSoundEffect,
} from '@/features/laneChooser/game/types';

type LaneChooserGameScreenStyles = CSSProperties & {
	'--lane-chooser-progress-end-image': string;
};

const INSTRUCTION_TOOLTIP_ANCHOR_ID = 'lane-chooser-instruction-tooltip-anchor';

type GameScreenProps = {
	gameVisible: boolean;
	questionsCount: number;
	totalPlayTime: number;
	currentQuestionIndex: number;
	answers: boolean[];
	gameStarted: boolean;
	timeAdjustSeconds: number;
	onGameStart: () => void;
	onBack: () => void;
	onTimeOut: () => void;
	laneLinesCount: number;
	question: LaneChooserQuestionPayload | null;
	answerResolution: LaneChooserAnswerResolution | null;
	onAnswerRequest: (selectedIndex: number) => void;
	onRoundExitComplete: () => void;
	onSoundEffect: (effect: LaneChooserSoundEffect) => void;
	onImagePreview?: (imageSrc: string) => void;
};

const GameScreen = forwardRef<HTMLDivElement, GameScreenProps>(
	(
		{
			gameVisible,
			questionsCount,
			totalPlayTime,
			currentQuestionIndex,
			answers,
			gameStarted,
			timeAdjustSeconds,
			onGameStart,
			onBack,
			onTimeOut,
			laneLinesCount,
			question,
			answerResolution,
			onAnswerRequest,
			onRoundExitComplete,
			onSoundEffect,
			onImagePreview,
		},
		ref,
	) => {
		const theme = useLaneChooserTheme();
		const { t } = useTranslation();
		const tooltipRef = useRef<HTMLDivElement | null>(null);
		const countdownStartedRef = useRef(false);
		const [instructionTooltipTextKey, setInstructionTooltipTextKey] = useState<string | null>(null);
		const handleCountdownFinish = useCallback(() => {
			countdownStartedRef.current = false;
			onGameStart();
		}, [onGameStart]);
		const { display: countdownDisplay, start: startCountdown } = useCountdown({
			initial: 3,
			onFinish: handleCountdownFinish,
			startDelayAfterEndMs: 500,
		});
		const gameScreenStyles: LaneChooserGameScreenStyles = {
			opacity: gameVisible ? 1 : 0,
			'--lane-chooser-progress-end-image': `url("${theme.images.progressEnd}")`,
		};

		const handleInstructionComplete = useCallback(() => {
			if (gameStarted || countdownStartedRef.current) {
				return;
			}

			countdownStartedRef.current = true;
			startCountdown();
		}, [gameStarted, startCountdown]);
		const handleInstructionTooltipPosition = useCallback((point: { x: number; y: number } | null) => {
			if (!tooltipRef.current) {
				return;
			}

			tooltipRef.current.style.transform = point
				? `translate(${point.x}px, ${point.y}px)`
				: 'translate(-9999px, -9999px)';
		}, []);

		return (
			<div className={`lane-chooser lane-chooser--${theme.key}`} style={gameScreenStyles} ref={ref} tabIndex={-1}>
				<div className="lane-chooser__instruction-tooltip-layer">
					<div
						ref={tooltipRef}
						id={INSTRUCTION_TOOLTIP_ANCHOR_ID}
						className="lane-chooser__instruction-tooltip-anchor"
					/>
					<Tooltip
						anchor={INSTRUCTION_TOOLTIP_ANCHOR_ID}
						title={instructionTooltipTextKey ? t(instructionTooltipTextKey) : ''}
						place="top"
						color={TooltipColors.LIGHT}
						size={TooltipSizes.LARGE}
						className="lane-chooser__instruction-tooltip"
						isOpen={Boolean(instructionTooltipTextKey)}
					/>
				</div>
				<Progress
					total={questionsCount}
					currentIndex={currentQuestionIndex}
					answers={answers}
					totalPlayTime={totalPlayTime}
					gameStarted={gameStarted}
					timeAdjustSeconds={timeAdjustSeconds}
					onTimeOut={onTimeOut}
					onBack={onBack}
				/>
				<LaneChooserStage
					className="lane-chooser__track"
					laneLinesCount={laneLinesCount}
					roundKey={currentQuestionIndex}
					question={question}
					gameStarted={gameStarted}
					answerResolution={answerResolution}
					onAnswerRequest={onAnswerRequest}
					onRoundExitComplete={onRoundExitComplete}
					onSoundEffect={onSoundEffect}
					onInstructionComplete={handleInstructionComplete}
					onInstructionTooltipChange={setInstructionTooltipTextKey}
					onInstructionTooltipPositionChange={handleInstructionTooltipPosition}
					onQuestionImagePreview={onImagePreview}
				/>
				{!gameStarted && countdownDisplay !== null && (
					<div className="lane-chooser__countdown">
						<div className="lane-chooser__countdown-number">{countdownDisplay}</div>
					</div>
				)}
			</div>
		);
	},
);

GameScreen.displayName = 'GameScreen';

export default GameScreen;
